import { useState } from "react";
import { useTonConnectUI, useTonAddress } from "@tonconnect/ui-react";
import { Address, beginCell, toNano } from "@ton/core";
import {
  AM_DISTRIBUTOR_OPS,
  buildAmClaimArgs,
  buildAmClaimBroadcastPayload,
  loadCommitmentsFromRelayer,
  serialiseGroth16Proof,
} from "@tonado/core";
import { NETWORK, RELAYER_URL } from "../lib/env";
import { parseNoteMetadata } from "../lib/note";
import { getPool } from "../lib/pools";

/**
 * Anonymity-mining claim flow. The user supplies the deposit note + the
 * AmDistributor address (one per pool denomination); the dApp generates
 * a Groth16 proof locally and submits via either a relayer (full
 * unlinkability) or self-submission (gas paid by the connected wallet,
 * publicly tied to the AM nullifier).
 */
export function AmClaim() {
  const [tonConnectUI] = useTonConnectUI();
  const sender = useTonAddress();
  const connected = Boolean(sender);

  const [note, setNote] = useState("");
  const [distributor, setDistributor] = useState("");
  const [recipient, setRecipient] = useState("");
  const [mode, setMode] = useState<"relay" | "self">("relay");
  const [relayerUrl, setRelayerUrl] = useState(RELAYER_URL);
  const [feeTonado, setFeeTonado] = useState("0");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [phase, setPhase] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      const distributorAddr = safeAddress(distributor);
      const recipientAddr = safeAddress(recipient);
      if (!distributorAddr) throw new Error("Invalid distributor address");
      if (!recipientAddr) throw new Error("Invalid recipient address");
      if (!note.trim()) throw new Error("Note is required");
      if (mode === "self" && !connected) throw new Error("Connect wallet for self-submit");

      // Pull the merkle leaves through the relayer's /tree/commitments
      // endpoint — the dApp never holds a toncenter API key, so all
      // chain reads go through the relayer's server-side client.
      const parsed = parseNoteMetadata(note.trim());
      if (!parsed) throw new Error("Could not parse the deposit note.");
      const pool = getPool(`${parsed.asset}-${parsed.amount}`);
      if (!pool) throw new Error(`No deployed pool for ${parsed.asset}-${parsed.amount}`);

      // For relay mode, ask the relayer for its address up-front so the
      // proof binds to it. For self mode, bind to the sender.
      let relayerAddr: Address;
      if (mode === "relay") {
        setPhase("Fetching relayer info…");
        const res = await fetch(`${relayerUrl}/status`);
        if (!res.ok) throw new Error(`Relayer /status HTTP ${res.status}`);
        const data = (await res.json()) as { address?: string };
        if (!data.address) throw new Error("Relayer /status missing 'address'");
        relayerAddr = Address.parse(data.address);
      } else {
        if (!sender) throw new Error("No sender available");
        relayerAddr = Address.parse(sender);
      }

      setPhase("Fetching commitments from relayer…");
      const preloadedCommitments = await loadCommitmentsFromRelayer(
        RELAYER_URL.replace(/\/+$/, ""),
        pool.address
      );

      setPhase("Generating AM proof (5-15s)…");
      const fee = parseTonadoAmount(feeTonado);
      const args = await buildAmClaimArgs({
        note: note.trim(),
        recipient: recipientAddr,
        relayer: relayerAddr,
        network: NETWORK,
        fee,
        preloadedCommitments,
      });

      if (mode === "relay") {
        setPhase("Submitting to relayer…");
        const payload = buildAmClaimBroadcastPayload({
          distributor: distributorAddr.toString(),
          args,
        });
        const resp = await fetch(`${relayerUrl}/claim-am`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          const errMsg = (data as { error?: string }).error ?? `HTTP ${resp.status}`;
          throw new Error(`Relayer rejected: ${errMsg}`);
        }
        setInfo(`Relayer accepted: ${JSON.stringify(data)}`);
      } else {
        setPhase("Submitting on-chain…");
        const proofCell = await serialiseGroth16Proof(args.proof);
        const coreCell = beginCell()
          .storeAddress(args.recipient)
          .storeAddress(args.relayer)
          .storeCoins(args.fee)
          .storeCoins(args.refund)
          .endCell();
        const body = beginCell()
          .storeUint(AM_DISTRIBUTOR_OPS.CLAIM, 32)
          .storeUint(0n, 64)
          .storeUint(args.root, 256)
          .storeUint(args.amNullifierHash, 256)
          .storeRef(coreCell)
          .storeRef(proofCell)
          .endCell();
        await tonConnectUI.sendTransaction({
          validUntil: Math.floor(Date.now() / 1000) + 360,
          messages: [
            {
              address: distributorAddr.toString(),
              amount: toNano("0.4").toString(),
              payload: body.toBoc().toString("base64"),
            },
          ],
        });
        setInfo("Claim submitted via your wallet.");
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      setPhase(null);
    }
  }

  return (
    <div className="flow flow--am">
      <h2 className="flow__title">Anonymity mining</h2>
      <p className="lede">
        Claim TONADO rewards for deposits you've made into a TONado pool. The proof uses a
        domain-separated nullifier so claiming does NOT consume the right to later withdraw the same
        deposit.
      </p>

      <label className="input-row">
        <span>Deposit note</span>
        <input
          type="password"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="tonado-ton-1-mainnet-..."
          className="mono mono--break"
        />
      </label>
      <label className="input-row">
        <span>AmDistributor address</span>
        <input
          type="text"
          value={distributor}
          onChange={(e) => setDistributor(e.target.value)}
          placeholder="EQ..."
          className="mono mono--break"
        />
      </label>
      <label className="input-row">
        <span>Recipient (where TONADO arrives)</span>
        <input
          type="text"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="EQ..."
          className="mono mono--break"
        />
      </label>

      <div className="tabs tabs--inline" role="tablist">
        {(["relay", "self"] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            className={`tabs__item ${mode === m ? "tabs__item--active" : ""}`}
            onClick={() => setMode(m)}
          >
            <span className="tabs__label">
              {m === "relay" ? "Via relayer (private)" : "Self-submit (links wallet)"}
            </span>
          </button>
        ))}
      </div>

      {mode === "relay" && (
        <>
          <label className="input-row">
            <span>Relayer URL</span>
            <input
              type="text"
              value={relayerUrl}
              onChange={(e) => setRelayerUrl(e.target.value)}
              className="mono mono--break"
            />
          </label>
          <label className="input-row">
            <span>Fee (TONADO)</span>
            <input
              type="text"
              value={feeTonado}
              onChange={(e) => setFeeTonado(e.target.value)}
              placeholder="0"
            />
          </label>
        </>
      )}

      <div className="cta-row">
        <button
          type="button"
          className="btn btn--primary btn--lg"
          disabled={busy || (mode === "self" && !connected)}
          onClick={handleSubmit}
        >
          {busy ? "Working…" : mode === "relay" ? "Claim via relayer" : "Claim self-submit"}
        </button>
      </div>

      {phase && (
        <div className="pending pending--multi">
          <span className="pending__dot" />
          <span>{phase}</span>
        </div>
      )}
      {error && (
        <p className="error">
          <strong>Error:</strong> {error}
        </p>
      )}
      {info && <p className="success__kicker">{info}</p>}
    </div>
  );
}

function safeAddress(s: string): Address | null {
  try {
    return Address.parse(s);
  } catch {
    return null;
  }
}

function parseTonadoAmount(input: string): bigint {
  if (!input.trim()) return 0n;
  if (/^\d+$/.test(input.trim())) return BigInt(input.trim());
  const [intPart, fracPart = ""] = input.split(".");
  const padded = (fracPart + "000000000").slice(0, 9);
  return BigInt(intPart || "0") * 10n ** 9n + BigInt(padded || "0");
}
